import { Col, Label, Row, SelectInput, TextInput } from '@digitools/honest-react';
import { useFormState } from 'informed';
import React from 'react';
import { CustomEmployeeText } from 'src/utils/CustomExperienceText';
import useExperience from 'src/hooks/useExperience';
import useTranslate from 'src/hooks/useTranslate';
import AboutYourAbsenceText from 'src/packages/cli/constants/AboutYourAbsenceText';
import useCliCustomerConfigContext from 'src/packages/cli/hooks/useCliContext';
import useCliDataContext from 'src/packages/cli/hooks/useCliDataContext';
import { LeaveType } from 'src/packages/cli/providers/CliConfigProvider';
import { Experience } from 'src/types/Experience';
//Relationship mappings - based on answer to absenceData.leaveReason
const fmcRelationshipMapping_CV = [
    'BP',
    'CH',
    'CP',
    'CD',
    'DP',
    'FP',
    'GC',
    'GP',
    'IP',
    'LG',
    'NK',
    'OT',
    'PL',
    'SI',
    'SP',
    'SC',
    'ST',
];
const fmcRelationshipMapping_DV = [
    'BP',
    'CH',
    'CP',
    'CD',
    'DP',
    'FP',
    'GC',
    'GP',
    'IP',
    'LG',
    'NK',
    'OT',
    'PL',
    'SI',
    'SP',
    'SC',
    'ST',
];
const fmcRelationshipMapping_SF = [
    'BP',
    'CH',
    'CP',
    'CD',
    'DP',
    'FP',
    'GC',
    'GP',
    'IP',
    'LG',
    'NK',
    'OT',
    'PL',
    'SI',
    'SP',
    'SC',
    'ST',
];
const fmcRelationshipMapping_SK = ['CH', 'CP', 'CD', 'GC', 'NK', 'SC'];
const fmcRelationshipMapping_CS = ['CH', 'SC', 'FC', 'IP'];
const fmcRelationshipMapping_NL = ['AC', 'BC', 'CD', 'FC', 'IP', 'LW', 'SC', 'CH', 'OT'];
const fmcRelationshipMapping_ZZ = [
    'BP', // BIOLOGICAL PARENT
    'CD', // CHILD OF DOMESTIC PARTNER
    'CH', // CHILD
    'CP', // CHILD 18+
    'DP', // DOMESTIC PARTNER
    'FP', // FOSTER PARENT
    'GC', // GRANDCHILD
    'GP', // GRANDPARENT
    'IP', // IN-LOCO PARENTIS
    'LG', // LEGAL GUARDIAN
    'NK', // NEXT OF KIN
    'OT', // OTHER
    'PL', // PARENT IN-LAW
    'SC', // STEP-CHILD
    'SI', // SIBLING
    'SP', // SPOUSE
    'ST', // STEP-PARENT
];
const fmcRelationshipMapping_BR = ['AC', 'BP', 'CD', 'CH', 'CP', 'DP', 'FP', 'GC', 'GP', 'IP', 'LG', 'PL', 'SI', 'SP', 'OT'];
const fmcRelationshipMapping_SE = ['AC', 'BP', 'CD', 'CH', 'CP', 'DP', 'FP', 'GC', 'GP', 'IP', 'LG', 'PL', 'SI', 'SP', 'OT'];
const fmcRelationshipMapping_WC = ['AC', 'BP', 'CD', 'CH', 'CP', 'DP', 'FP', 'GC', 'GP', 'IP', 'LG', 'PL', 'SI', 'SP', 'OT'];
// Parental mappings
const bndRelationshipMapping_NM = ['BC', 'CD', 'LW', 'NK', 'SC'];
const bndRelationshipMapping_NP = ['BC', 'CD', 'LW', 'NK', 'SC'];
const bndRelationshipMapping_OT = ['NK', 'OT', 'IP'];
const bndRelationshipMapping_SS = ['AC', 'BC', 'CD', 'FC', 'LW', 'NK', 'SC'];
// Military mappings
const xmlRelationshipMapping_ML = [
    'BP',
    'CH',
    'CP',
    'CD',
    'DP',
    'FP',
    'GC',
    'GP',
    'IP',
    'LG',
    'NK',
    'OT',
    'PL',
    'SI',
    'SP',
    'SC',
    'ST',
];
const othRelationshipMapping_SC = ['CH', 'CP', 'CD', 'FC', 'GC', 'LW', 'SC'];
const othRelationshipMapping_WC = [
    'BP',
    'CH',
    'CP',
    'CD',
    'FC',
    'FP',
    'GC',
    'GP',
    'IP',
    'LG',
    'LW',
    'PL',
    'SP',
    'SC',
    'ST',
    'OT'
];
const getRelationshipDisplayOptions = (t, relationships, leaveType, leaveReason) => {
    let potentialList = [];
    switch (leaveType) {
        case LeaveType.FAMILY_CARE:
            switch (leaveReason) {
                case 'DV':
                    potentialList = fmcRelationshipMapping_DV;
                    break;
                case 'SK':
                    potentialList = fmcRelationshipMapping_SK;
                    break;
                case 'CS':
                    potentialList = fmcRelationshipMapping_CS;
                    break;
                case 'SF':
                    potentialList = fmcRelationshipMapping_SF;
                    break;
                case 'NL':
                    potentialList = fmcRelationshipMapping_NL;
                    break;
                case 'CV':
                    potentialList = fmcRelationshipMapping_CV;
                    break;
                case 'BR':
                    potentialList = fmcRelationshipMapping_BR;
                    break;
                case 'SE':
                    potentialList = fmcRelationshipMapping_SE;
                    break;
                case 'WC':
                    potentialList = fmcRelationshipMapping_WC;
                    break;
                case 'ZZ':
                    potentialList = fmcRelationshipMapping_ZZ;
                    break;
                default:
                    break;
            }
            break;
        case LeaveType.PARENTAL:
            switch (leaveReason) {
                case 'NM':
                    potentialList = bndRelationshipMapping_NM;
                    break;
                case 'NP':
                    potentialList = bndRelationshipMapping_NP;
                    break;
                case 'OT':
                    potentialList = bndRelationshipMapping_OT;
                    break;
                case 'SS':
                    potentialList = bndRelationshipMapping_SS;
                    break;
                default:
                    break;
            }
            break;
        case LeaveType.MILITARY:
            switch (leaveReason) {
                case 'ML':
                    potentialList = xmlRelationshipMapping_ML;
                    break;
                default:
                    break;
            }
            break;
        case LeaveType.OTHER:
            switch (leaveReason) {
                case 'SC':
                    potentialList = othRelationshipMapping_SC;
                    break;
                case 'WC':
                    potentialList = othRelationshipMapping_WC;
                    break;
                default:
                    break;
            }
            break;
        // case LeaveType.OWN_ILLNESS:
        default:
            break;
    }
    // Filter out only codes found in the potention list
    const leaveReasonOptions = relationships.filter(relationship => potentialList.includes(relationship.code));
    // Convert to display options for input
    const leaveReasonDisplayOptions = leaveReasonOptions.map(leaveReasonOption => {
        return {
            label: t(leaveReasonOption),
            value: leaveReasonOption.code,
        };
    });
    return leaveReasonDisplayOptions;
};
const Relationship = () => {
    const { t } = useTranslate();
    const { absenceData } = useCliDataContext();
    const { relationships } = useCliCustomerConfigContext();
    const { values } = useFormState();
    const { experience } = useExperience();
    //@ts-ignore
    const leaveType = values?.absenceData?.leaveType;
    //@ts-ignore
    const leaveReason = values?.absenceData?.leaveReason;
    //@ts-ignore
    const leaveCategory = values?.absenceData?.leaveCategory;
    const options = getRelationshipDisplayOptions(t, relationships, leaveType, leaveReason);
    return (<>
      {/* default hidden value of 'AC' for leaveType of Parental with a leaveReason of AD */}
      {leaveReason === 'AD' && leaveType === LeaveType.PARENTAL && (leaveCategory === 'C' || leaveCategory === 'I') && (<TextInput hidden={true} skinny={true} hideLabel={true} label={''} field='relationship' initialValue={'AC'}/>)}
      {/* default hidden value of 'FC' for leaveType of Parental with a leaveReason of FC */}
      {leaveReason === 'FC' && leaveType === LeaveType.PARENTAL && (leaveCategory === 'C' || leaveCategory === 'I') && (<TextInput hidden={true} skinny={true} hideLabel={true} label={''} field='relationship' initialValue={'FC'}/>)}
      <Row className='mb-3'>
        <Col>
          {!(leaveReason === 'AD' || leaveReason === 'FC') && (<>
              <Label className='mb-2' htmlFor='relationship'>
                {CustomEmployeeText(t(experience === Experience.EMPLOYEE
                ? AboutYourAbsenceText.formLabels.relationshipEE
                : AboutYourAbsenceText.formLabels.relationshipER))}
              </Label>
              <SelectInput required={true} field={'relationship'} label={t({ english: 'Relationship', spanish: 'relación' })} options={options} data-testid={'relationship'} validateOnBlur={true} initialValue={absenceData?.relationship} data-private={true}/>
            </>)}
        </Col>
      </Row>
    </>);
};
export default Relationship;
